import {useCartContext} from 'components/providers';
import {useCallback, useEffect, useRef, useState} from 'react';
import {ICartItem, IProduct, IRestaurantResponse} from 'types';
import {v4 as uuidv4} from 'uuid';

interface UseDebouncedCartUpdateProps {
  product: IProduct;
  provider?: IRestaurantResponse;
  initialQuantity: number;
  cartItemId?: string;
  onOpenAlertClearCart?: () => void;
  debounceDelay?: number;
}

interface PendingOperation {
  type: 'add' | 'remove' | 'set';
  quantity: number;
  timestamp: number;
}

export const useDebouncedCartUpdate = ({
  product,
  provider,
  initialQuantity,
  cartItemId,
  onOpenAlertClearCart,
  debounceDelay = 300,
}: UseDebouncedCartUpdateProps) => {
  const [localQuantity, setLocalQuantity] = useState(initialQuantity);
  const [isUpdating, setIsUpdating] = useState(false);

  const pendingOperationRef = useRef<PendingOperation | null>(null);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  const {addProductToCart, changeCartItemQuantity, deleteProduct, addProvider, cartProvider} = useCartContext();

  const step = product.is_weighted_product ? 0.5 : 1;

  useEffect(() => {
    if (!isUpdating) {
      setLocalQuantity(initialQuantity);
    }
  }, [initialQuantity, isUpdating]);

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  const executeCartUpdate = useCallback(
    async (operation: PendingOperation) => {
      setIsUpdating(true);

      try {
        if (operation.type === 'add' || operation.type === 'set') {
          if (operation.quantity > initialQuantity && cartItemId) {
            changeCartItemQuantity(cartItemId, +operation.quantity.toFixed(1));
          } else if (operation.quantity > 0 && !cartItemId) {
            let added_from = 'restaurant/';
            const isRecommended = provider?.recommended_products?.some((p) => p.id === product.id);
            added_from += isRecommended ? 'restaurant_recommended' : 'restaurant';

            const newCartItem: ICartItem = {
              id: uuidv4(),
              product: {
                ...product,
                price: Number(product.price),
                discount: 0,
                discount_price: Number(product.price),
                mass_measurement: product.mass_measurement,
              },
              productId: product.id,
              price: Number(product.price),
              quantity: operation.quantity,
              instructions: '',
              selectedOptions: {},
              added_from,
            };

            if (cartProvider?.slug && provider?.slug !== cartProvider.slug) {
              onOpenAlertClearCart?.();
              return;
            }

            addProductToCart(newCartItem);
            provider && addProvider(provider);
          }
        } else if (operation.type === 'remove') {
          if (cartItemId) {
            if (operation.quantity <= 0) {
              deleteProduct(cartItemId);
            } else {
              changeCartItemQuantity(cartItemId, +operation.quantity.toFixed(1));
            }
          }
        }
      } catch (error) {
        console.error('Failed to update cart:', error);

        setLocalQuantity(initialQuantity);
      } finally {
        setIsUpdating(false);
        pendingOperationRef.current = null;
      }
    },
    [
      product,
      provider,
      cartProvider?.slug,
      onOpenAlertClearCart,
      addProductToCart,
      changeCartItemQuantity,
      deleteProduct,
      addProvider,
      cartItemId,
      initialQuantity,
    ],
  );

  const scheduleUpdate = useCallback(
    (operation: PendingOperation) => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }

      pendingOperationRef.current = operation;

      timeoutRef.current = setTimeout(() => {
        if (pendingOperationRef.current) {
          executeCartUpdate(pendingOperationRef.current);
        }
      }, debounceDelay);
    },
    [executeCartUpdate, debounceDelay],
  );

  const handleAdd = useCallback(() => {
    if (localQuantity >= (product.stock_quantity || 0)) {
      return;
    }

    const newQuantity = +(localQuantity + step).toFixed(1);
    setLocalQuantity(newQuantity);

    scheduleUpdate({
      type: 'add',
      quantity: newQuantity,
      timestamp: Date.now(),
    });
  }, [localQuantity, product.stock_quantity, step, scheduleUpdate]);

  const handleRemove = useCallback(() => {
    const newQuantity = Math.max(0, +(localQuantity - step).toFixed(1));
    setLocalQuantity(newQuantity);

    scheduleUpdate({
      type: 'remove',
      quantity: newQuantity,
      timestamp: Date.now(),
    });
  }, [localQuantity, step, scheduleUpdate]);

  const handleSetQuantity = useCallback(
    (quantity: number) => {
      const clampedQuantity = Math.max(0, Math.min(quantity, product.stock_quantity || 0));
      setLocalQuantity(clampedQuantity);

      scheduleUpdate({
        type: 'set',
        quantity: clampedQuantity,
        timestamp: Date.now(),
      });
    },
    [product.stock_quantity, scheduleUpdate],
  );

  const flushPendingUpdates = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    if (pendingOperationRef.current) {
      executeCartUpdate(pendingOperationRef.current);
    }
  }, [executeCartUpdate]);

  return {
    localQuantity,
    isUpdating,
    isSelected: localQuantity > 0,
    handleAdd,
    handleRemove,
    handleSetQuantity,
    flushPendingUpdates,
    step,
  };
};
